import { useMemo } from "react";
import { SPACING } from "../../constants/design/spacing";
import { TEXT_COLORS } from "../../constants/design/colors";
import { useAccountIsolatedHealth } from "../../hooks/api/admin/useAccountIsolatedHealth";
import { formatDecimalString } from "../../utils/decimal";
import { KeyValue } from "./stats";
import { StatTitle } from "./stats/style";
import { IsolatedHealthTable } from "./tables/IsolatedHealthTable";

interface IIsolatedHealthProps {
  isMobile?: boolean;
}

export function IsolatedHealth({ isMobile }: IIsolatedHealthProps) {
  const {
    data: isolatedHealth,
    isLoading,
    isValidating,
  } = useAccountIsolatedHealth();
  const summary = isolatedHealth?.summary;
  const isTableLoading = isLoading || (!isLoading && isValidating);

  const summaryCards = useMemo(
    () => [
      {
        title: "Isolated Positions",
        value: formatDecimalString(summary?.isolated_positions_count || 0, {
          maxFractionDigits: 0,
          fallback: "0",
        }),
      },
      {
        title: "Breached Positions",
        value: formatDecimalString(summary?.breached_positions_count || 0, {
          maxFractionDigits: 0,
          fallback: "0",
        }),
      },
      {
        title: "Liquidating Positions",
        value: formatDecimalString(summary?.liquidating_positions_count || 0, {
          maxFractionDigits: 0,
          fallback: "0",
        }),
      },
    ],
    [
      summary?.breached_positions_count,
      summary?.isolated_positions_count,
      summary?.liquidating_positions_count,
    ]
  );

  const assignedIsolatedMargin = formatDecimalString(
    summary?.assigned_isolated_margin,
    {
      maxFractionDigits: 4,
    }
  );
  const isolatedAvailableMargin = formatDecimalString(
    summary?.isolated_available_margin,
    {
      maxFractionDigits: 4,
    }
  );

  return (
    <div style={{ margin: isMobile ? 0 : SPACING.two }}>
      <StatTitle isMobile={isMobile}>Isolated Margin Health</StatTitle>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
          gap: SPACING.two,
          margin: `0 ${SPACING.two}px ${SPACING.two}px`,
        }}
      >
        {summaryCards.map((summaryCard) => (
          <KeyValue
            key={summaryCard.title}
            title={summaryCard.title}
            value={summaryCard.value}
          />
        ))}
      </div>

      <div
        style={{
          margin: `0 ${SPACING.two}px ${SPACING.two}px`,
          color: TEXT_COLORS.two,
          fontSize: 12,
          display: "flex",
          gap: SPACING.three,
          flexWrap: "wrap",
        }}
      >
        <span>
          Assigned Isolated Margin:{" "}
          <span style={{ color: TEXT_COLORS.one }}>
            {assignedIsolatedMargin === "-" ? "-" : `$${assignedIsolatedMargin}`}
          </span>
        </span>
        <span>
          Isolated Available Margin:{" "}
          <span style={{ color: TEXT_COLORS.one }}>
            {isolatedAvailableMargin === "-" ? "-" : `$${isolatedAvailableMargin}`}
          </span>
        </span>
      </div>

      <div style={{ margin: `0 ${SPACING.two}px` }}>
        <IsolatedHealthTable
          positions={isolatedHealth?.positions || []}
          isLoading={isTableLoading}
        />
      </div>
    </div>
  );
}
